import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { captureEvent } from "@/lib/analyticsQueue";
import { toast } from "@/lib/toast";
import { useCredits } from "@/contexts/CreditsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import {
  createPayment,
  validateCoupon,
  reactivateSubscription,
  scheduleDowngrade as scheduleDowngradeApi,
  cancelScheduledDowngrade,
  type ValidateCouponResponse,
} from "@/lib/api/payments";
import { PURCHASES_ENABLED } from "@/config/featureFlags";
import { PLANS, getPlanPrice, getPlanPriceNIS, DEBUG_PLAN_ID } from "@/config/plans";
import type { PlanId, TokenPackage } from "@/config/plans";
import { USER_ROLES } from "@/config/admin";
import type { CheckoutLocationState, CheckoutPurchase } from "@/pages/Checkout/types";

/** Currency symbol used on all about-to-be-charged surfaces. PayPlus charges in ILS regardless of UI language. */
const ILS_SYMBOL = "₪";
import { COUPON_BLOCKED_FOR_ANNUAL } from "@/config/pricing";

/**
 * Determines whether a plan change is an upgrade (charge now) or downgrade (schedule at period end).
 * Upgrade = higher tier, OR same tier switching monthly → annual.
 */
function isUpgradeChange(
  currentPlanId: string,
  currentBillingPeriod: string,
  newPlanId: string,
  newBillingPeriod: string,
): boolean {
  const currentIndex = PLANS.findIndex((p) => p.id === currentPlanId);
  const newIndex = PLANS.findIndex((p) => p.id === newPlanId);
  if (newIndex > currentIndex) return true;
  if (newIndex === currentIndex && currentBillingPeriod === "monthly" && newBillingPeriod === "annual") return true;
  return false;
}
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { capturePaymentError } from "@/lib/sentry";

interface PendingDowngrade {
  planId: string;
  planName: string;
  billingCycle: "monthly" | "annual";
}

interface PendingReactivation {
  planId: string;
  planName: string;
  billingCycle: "monthly" | "annual";
  isPlanChange: boolean;
}

export function usePricing() {
  const [showTokensDialog, setShowTokensDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingDowngrade, setPendingDowngrade] = useState<PendingDowngrade | null>(null);
  const [pendingReactivation, setPendingReactivation] = useState<PendingReactivation | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCancelScheduledDialog, setShowCancelScheduledDialog] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState<ValidateCouponResponse | null>(null);
  const { refreshCredits, setShowPricingModal } = useCredits();
  const { user, refreshProfile, profile } = useAuth();
  const { t, language } = useLanguage();
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // External entry-point: `/pricing?dialog=tokens` auto-opens the credit-pack
  // dialog (used by the Bot Builder when a deposit hits insufficient main
  // credits — see useBotBuilder.depositToBot). The param is consumed on
  // mount and stripped from the URL so back-nav lands on a clean state.
  useEffect(() => {
    if (searchParams.get("dialog") === "tokens") {
      setShowTokensDialog(true);
      const next = new URLSearchParams(searchParams);
      next.delete("dialog");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const currentPlanId = profile?.subscription_name ?? null;
  const currentBillingPeriod = profile?.billing_period ?? null;
  const subscriptionStatus = profile?.subscription_status ?? null;
  const currentPeriodStart = profile?.current_period_start ?? null;
  const currentPeriodEnd = profile?.current_period_end ?? null;
  const scheduledPlanId = profile?.scheduled_plan_id ?? null;
  const scheduledBillingPeriod = profile?.scheduled_billing_period ?? null;

  const proceedToPayment = useCallback(async (
    planId: string,
    billingCycle: "monthly" | "annual",
    proration?: {
      proratedPrice: number;
      fullPrice: number;
      remainingDays: number;
      nextBillingDate?: string;
      currentPlanName: string;
      currentPlanPriceDisplay: string;
      creditedAmount: number;
      newPlanForRemainingDays: number;
    },
  ) => {
    // Wind-down: no new charges (server also returns 403 PAYMENTS_CLOSED).
    if (!PURCHASES_ENABLED) {
      toast.info(t.pricing.purchasesClosed);
      return;
    }
    setIsProcessing(true);

    captureEvent(ANALYTICS_EVENTS.PAYMENT_INITIATED, {
      plan_id: planId,
      billing_period: billingCycle,
    });

    try {
      const response = await createPayment({
        type: "subscription",
        planId,
        billingPeriod: billingCycle,
        ...(couponResult?.valid && couponCode && !(COUPON_BLOCKED_FOR_ANNUAL && billingCycle === "annual") ? { couponCode } : {}),
      });

      captureEvent(ANALYTICS_EVENTS.PAYMENT_REDIRECT, {
        plan_id: planId,
        billing_period: billingCycle,
      });

      const plan = PLANS.find((p) => p.id === planId);
      const planName = plan?.name ?? planId;
      const periodLabel = billingCycle === "annual" ? t.pricing.periodYear : t.pricing.periodMonth;

      let purchase: CheckoutPurchase;
      if (proration) {
        // Prorated upgrade — server charges proratedPrice today, then fullPrice every cycle starting at the next billing date.
        purchase = {
          kind: "subscription",
          planId: planId as PlanId,
          planName,
          billingCycle,
          priceDisplay: `${ILS_SYMBOL}${proration.proratedPrice}`,
          recurringPriceDisplay: `${ILS_SYMBOL}${proration.fullPrice}/${periodLabel}`,
          remainingDaysCovered: proration.remainingDays,
          ...(proration.nextBillingDate ? { nextBillingDate: proration.nextBillingDate } : {}),
          prorationBreakdown: {
            currentPlanName: proration.currentPlanName,
            currentPlanPriceDisplay: proration.currentPlanPriceDisplay,
            creditedAmount: `${ILS_SYMBOL}${proration.creditedAmount}`,
            newPlanForRemainingDays: `${ILS_SYMBOL}${proration.newPlanForRemainingDays}`,
            remainingDays: proration.remainingDays,
          },
        };
      } else {
        const fullPrice = getPlanPriceNIS(planId as PlanId, billingCycle);
        const couponApplies =
          couponResult?.valid && couponCode && !(COUPON_BLOCKED_FOR_ANNUAL && billingCycle === "annual");
        const discountPercent = couponApplies ? couponResult?.discountPercent ?? 0 : 0;
        const discountAmount = couponApplies ? couponResult?.discountAmount ?? 0 : 0;
        // Mirror server formula in paymentService.ts (Math.ceil for percent, plain subtraction for amount).
        const firstChargePrice =
          discountPercent > 0
            ? Math.max(0, Math.ceil(fullPrice * (1 - discountPercent / 100)))
            : discountAmount > 0
              ? Math.max(0, fullPrice - discountAmount)
              : fullPrice;
        const hasDiscount = firstChargePrice !== fullPrice;
        purchase = {
          kind: "subscription",
          planId: planId as PlanId,
          planName,
          billingCycle,
          priceDisplay: `${ILS_SYMBOL}${firstChargePrice}`,
          ...(hasDiscount
            ? { recurringPriceDisplay: `${ILS_SYMBOL}${fullPrice}/${periodLabel}` }
            : {}),
          ...(couponApplies
            ? {
                couponCode,
                ...(discountAmount > 0 ? { couponDiscountAmount: discountAmount } : {}),
                ...(discountPercent > 0 ? { couponDiscountPercent: discountPercent } : {}),
              }
            : {}),
        };
      }
      const checkoutState: CheckoutLocationState = {
        paymentUrl: response.paymentUrl,
        pageRequestUid: response.pageRequestUid,
        purchase,
      };
      // Close the credit-wall modal (if open) before leaving so it doesn't
      // linger over the checkout page. No-op on the /pricing page.
      setShowPricingModal(false);
      navigate("/checkout", { state: checkoutState });
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/create-payment", action: "create_payment" });
      setIsProcessing(false);
      const message =
        error instanceof Error ? error.message : t.paymentResult.defaultError;
      toast.error(message);
      captureEvent(ANALYTICS_EVENTS.PAYMENT_CREATION_FAILED, {
        plan_id: planId,
        billing_period: billingCycle,
        error_type: error instanceof Error ? error.message : "unknown",
      });
    }
  }, [t, couponResult, couponCode, navigate, setShowPricingModal]);

  const handleApplyCoupon = useCallback(async () => {
    const trimmed = couponCode.trim();
    if (!trimmed) return;

    if (!user) {
      openAuthModal("signup");
      return;
    }

    setCouponLoading(true);
    captureEvent(ANALYTICS_EVENTS.COUPON_APPLIED, { coupon_code: trimmed });
    try {
      const result = await validateCoupon(trimmed);
      setCouponResult(result);
      if (result.valid) {
        captureEvent(ANALYTICS_EVENTS.COUPON_VALID, {
          discount_percent: result.discountPercent,
          discount_amount: result.discountAmount,
        });
      } else {
        captureEvent(ANALYTICS_EVENTS.COUPON_INVALID, {
          reason: result.message ?? "invalid",
        });
        toast.error(result.message ?? t.pricing.couponInvalid);
      }
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/validate-coupon", action: "validate_coupon" });
      captureEvent(ANALYTICS_EVENTS.COUPON_INVALID, { reason: "api_error" });
      toast.error(t.pricing.couponError);
      setCouponResult(null);
    } finally {
      setCouponLoading(false);
    }
  }, [couponCode, t, user, openAuthModal]);

  const handleClearCoupon = useCallback(() => {
    setCouponCode("");
    setCouponResult(null);
  }, []);

  const handleCancelComplete = useCallback(async () => {
    setShowCancelDialog(false);
    await refreshProfile();
    await refreshCredits();
  }, [refreshProfile, refreshCredits]);

  const handleCancelDialogClose = useCallback(() => {
    setShowCancelDialog(false);
  }, []);

  const handleSelectPlan = async (
    planId: string,
    planName: string,
    billingCycle: "monthly" | "annual",
  ) => {
    // All plans require login
    if (!user) {
      openAuthModal("signup");
      return;
    }

    // Free plan: confirm if downgrading from paid, guard if already cancelled
    if (planId === "free") {
      if (subscriptionStatus === "cancelled") {
        toast.info(t.pricing.alreadyDowngradingToFree);
        return;
      }
      if (subscriptionStatus === "active" && currentPlanId && currentPlanId !== "free") {
        setShowCancelDialog(true);
        return;
      }
      return;
    }

    // Same-plan guard: prevent re-purchasing the current active plan
    if (
      subscriptionStatus === "active" &&
      currentPlanId === planId &&
      currentBillingPeriod === billingCycle
    ) {
      toast.info(t.pricing.alreadyOnPlan);
      return;
    }

    // Already-scheduled guard: prevent re-scheduling the same downgrade
    if (scheduledPlanId === planId && scheduledBillingPeriod === billingCycle) {
      toast.info(t.pricing.alreadyScheduled);
      return;
    }

    // Helper: compute proration for an upgrade and either send the user to /checkout
    // (when there's a real charge) or fall through to the caller's "covered by credit" handler.
    const startProratedUpgrade = async (
      planEnd: number,
    ): Promise<"charged" | "covered"> => {
      if (!currentPeriodStart) return "covered";
      const now = Date.now();
      const start = new Date(currentPeriodStart).getTime();
      const remainingDays = Math.max(1, Math.ceil((planEnd - now) / (1000 * 60 * 60 * 24)));
      const remainingRatio = Math.max(0, (planEnd - now) / (planEnd - start));
      const oldPrice = getPlanPriceNIS(currentPlanId as PlanId, currentBillingPeriod as "monthly" | "annual");
      const remainingValue = oldPrice * remainingRatio;
      const newPrice = getPlanPriceNIS(planId as PlanId, billingCycle);
      const newDailyRate = billingCycle === "annual" ? newPrice / 365 : newPrice / 30;
      const newCostForRemaining = newDailyRate * remainingDays;
      const rawProrated = Math.ceil(newCostForRemaining - remainingValue);

      if (rawProrated <= 0) return "covered";

      captureEvent(ANALYTICS_EVENTS.UPGRADE_CONFIRMED, {
        plan_id: planId,
        billing_period: billingCycle,
        prorated_price: rawProrated,
      });

      const currentPlanRow = PLANS.find((p) => p.id === currentPlanId);
      const currentPlanName = currentPlanRow?.name ?? currentPlanId ?? "";
      const currentPeriodLabel = currentBillingPeriod === "annual" ? t.pricing.periodYear : t.pricing.periodMonth;
      const nextBillingDate = currentPeriodEnd
        ? new Date(currentPeriodEnd).toLocaleDateString(
            language === "he" ? "he-IL" : "en-US",
            { year: "numeric", month: "long", day: "numeric" },
          )
        : undefined;

      await proceedToPayment(planId, billingCycle, {
        proratedPrice: rawProrated,
        fullPrice: newPrice,
        remainingDays,
        nextBillingDate,
        currentPlanName,
        currentPlanPriceDisplay: `${ILS_SYMBOL}${oldPrice}/${currentPeriodLabel}`,
        creditedAmount: Math.round(remainingValue),
        newPlanForRemainingDays: Math.round(newCostForRemaining),
      });
      return "charged";
    };

    // Upgrade/downgrade detection for active subscribers
    if (subscriptionStatus === "active" && currentPlanId && currentPlanId !== "free") {
      const upgrade = isUpgradeChange(currentPlanId, currentBillingPeriod ?? "monthly", planId, billingCycle);

      if (upgrade && currentPeriodStart && currentPeriodEnd) {
        const result = await startProratedUpgrade(new Date(currentPeriodEnd).getTime());
        if (result === "charged") return;
        // Remaining value covers the new plan — schedule at period end, no charge
        setPendingDowngrade({ planId, planName, billingCycle });
        return;
      }

      if (!upgrade) {
        // Downgrade — schedule at period end, no charge
        setPendingDowngrade({ planId, planName, billingCycle });
        return;
      }
    }

    // Cancelled subscription with remaining time: reactivation or prorated upgrade
    if (subscriptionStatus === "cancelled" && currentPlanId && currentPlanId !== "free" && currentPeriodEnd) {
      const periodEndTime = new Date(currentPeriodEnd).getTime();
      const hasRemainingTime = periodEndTime > Date.now();

      if (hasRemainingTime) {
        const upgrade = isUpgradeChange(currentPlanId, currentBillingPeriod ?? "monthly", planId, billingCycle);

        if (upgrade && currentPeriodStart) {
          const result = await startProratedUpgrade(periodEndTime);
          if (result === "charged") return;
          // Remaining value covers the new plan — free reactivation, no charge
          const isPlanChange = currentPlanId !== planId || currentBillingPeriod !== billingCycle;
          setPendingReactivation({ planId, planName, billingCycle, isPlanChange });
          return;
        }

        // All other cases with remaining time: free reactivation (no charge)
        // Covers: same plan+period, same plan+different period, downgrade, cross-period changes
        const isPlanChange = currentPlanId !== planId || currentBillingPeriod !== billingCycle;
        setPendingReactivation({ planId, planName, billingCycle, isPlanChange });
        return;
      }
      // else: period ended — fall through to full payment
    }

    await proceedToPayment(planId, billingCycle);
  };

  const handleConfirmDowngrade = useCallback(async () => {
    if (!pendingDowngrade) return;
    const { planId, billingCycle } = pendingDowngrade;
    setPendingDowngrade(null);
    setIsProcessing(true);
    captureEvent(ANALYTICS_EVENTS.DOWNGRADE_CONFIRMED, {
      plan_id: planId,
      billing_period: billingCycle,
    });
    try {
      await scheduleDowngradeApi({ planId, billingPeriod: billingCycle });
      await refreshProfile();
      toast.success(t.pricing.downgradeScheduledSuccess);
      captureEvent(ANALYTICS_EVENTS.DOWNGRADE_CONFIRMED, {
        plan_id: planId,
        billing_period: billingCycle,
        scheduled: true,
      });
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/schedule-downgrade", action: "schedule_downgrade" });
      const message = error instanceof Error ? error.message : t.paymentResult.defaultError;
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  }, [pendingDowngrade, refreshProfile, t]);

  const handleCancelDowngrade = useCallback(() => {
    setPendingDowngrade(null);
  }, []);

  const handleConfirmReactivation = useCallback(async () => {
    if (!pendingReactivation) return;
    const { planId, billingCycle, isPlanChange } = pendingReactivation;
    setPendingReactivation(null);
    setIsProcessing(true);
    captureEvent(ANALYTICS_EVENTS.REACTIVATION_CONFIRMED, {
      plan_id: planId,
      billing_period: billingCycle,
      is_plan_change: isPlanChange,
    });
    try {
      await reactivateSubscription(
        isPlanChange ? { planId, billingPeriod: billingCycle } : undefined,
      );
      await refreshProfile();
      await refreshCredits();
      toast.success(t.pricing.reactivationSuccess);
      captureEvent(ANALYTICS_EVENTS.REACTIVATION_SUCCEEDED, {
        plan_id: planId,
        billing_period: billingCycle,
        is_plan_change: isPlanChange,
      });
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/reactivate-subscription", action: "reactivate_subscription" });
      captureEvent(ANALYTICS_EVENTS.REACTIVATION_FAILED, {
        plan_id: planId,
        billing_period: billingCycle,
        error_type: error instanceof Error ? error.message : "unknown",
      });
      const message = error instanceof Error ? error.message : t.paymentResult.defaultError;
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  }, [pendingReactivation, refreshProfile, refreshCredits, t]);

  const handleCancelReactivation = useCallback(() => {
    setPendingReactivation(null);
  }, []);

  const handleAddTokens = () => {
    setShowTokensDialog(true);
  };

  const handlePurchaseTokens = async (tokens: number, priceNIS: number) => {
    if (!user) {
      openAuthModal("signup");
      return;
    }

    // Wind-down: no new charges (server also returns 403 PAYMENTS_CLOSED).
    if (!PURCHASES_ENABLED) {
      toast.info(t.pricing.purchasesClosed);
      return;
    }

    setIsProcessing(true);

    captureEvent(ANALYTICS_EVENTS.PAYMENT_INITIATED, {
      type: "token_pack",
      tokens,
    });

    try {
      const response = await createPayment({
        type: "token_pack",
        tokens,
      });

      captureEvent(ANALYTICS_EVENTS.PAYMENT_REDIRECT, {
        type: "token_pack",
        tokens,
      });

      const purchase: CheckoutPurchase = {
        kind: "tokens",
        tokens,
        priceDisplay: `${ILS_SYMBOL}${priceNIS}`,
      };
      const checkoutState: CheckoutLocationState = {
        paymentUrl: response.paymentUrl,
        pageRequestUid: response.pageRequestUid,
        purchase,
      };
      setShowPricingModal(false);
      navigate("/checkout", { state: checkoutState });
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/create-payment", action: "purchase_tokens" });
      setIsProcessing(false);
      const message =
        error instanceof Error ? error.message : t.paymentResult.defaultError;
      toast.error(message);
      captureEvent(ANALYTICS_EVENTS.PAYMENT_CREATION_FAILED, {
        type: "token_pack",
        tokens,
        error_type: error instanceof Error ? error.message : "unknown",
      });
    }
  };

  // Dev-only ₪1 recurring debug purchase — visible only to admins (profile.role === 'admin').
  // Server enforces the boundary via the same role check; this is the UI entry point.
  // Uses createPayment directly (not proceedToPayment) because the debug plan is
  // intentionally NOT in the PLANS array, so the standard upgrade/proration helpers don't apply.
  const isDebugUser = profile?.role === USER_ROLES.ADMIN;
  const handleDebugPurchase = useCallback(async () => {
    if (!user) {
      openAuthModal("signup");
      return;
    }
    if (!PURCHASES_ENABLED) {
      toast.info(t.pricing.purchasesClosed);
      return;
    }
    setIsProcessing(true);
    captureEvent(ANALYTICS_EVENTS.PAYMENT_INITIATED, {
      plan_id: DEBUG_PLAN_ID,
      billing_period: "monthly",
    });
    try {
      const response = await createPayment({
        type: "subscription",
        planId: DEBUG_PLAN_ID,
        billingPeriod: "monthly",
      });
      const purchase: CheckoutPurchase = {
        kind: "subscription",
        planId: DEBUG_PLAN_ID,
        planName: "DEBUG",
        billingCycle: "monthly",
        priceDisplay: `${ILS_SYMBOL}1`,
      };
      const checkoutState: CheckoutLocationState = {
        paymentUrl: response.paymentUrl,
        pageRequestUid: response.pageRequestUid,
        purchase,
      };
      setShowPricingModal(false);
      navigate("/checkout", { state: checkoutState });
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/create-payment", action: "create_payment_debug" });
      setIsProcessing(false);
      const message = error instanceof Error ? error.message : t.paymentResult.defaultError;
      toast.error(message);
    }
  }, [user, openAuthModal, navigate, t, setShowPricingModal]);

  // PayPlus charges in ILS, so token-pack prices on the picker dialog are always shown in NIS.
  const getTokenPriceDisplay = (pkg: TokenPackage) => pkg.priceNIS;
  const getTokenOriginalPriceDisplay = (pkg: TokenPackage) => pkg.originalPriceNIS;

  const handleCancelScheduledChange = useCallback(() => {
    setShowCancelScheduledDialog(true);
  }, []);

  const confirmCancelScheduledChange = useCallback(async () => {
    setShowCancelScheduledDialog(false);
    setIsProcessing(true);
    try {
      await cancelScheduledDowngrade();
      await refreshProfile();
      toast.success(t.pricing.cancelScheduledChangeSuccess);
    } catch (error) {
      capturePaymentError(error, { endpoint: "/api/payments/cancel-scheduled-downgrade", action: "cancel_scheduled_downgrade" });
      const message = error instanceof Error ? error.message : t.paymentResult.defaultError;
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  }, [refreshProfile, t]);

  const handleCloseCancelScheduledDialog = useCallback(() => {
    setShowCancelScheduledDialog(false);
  }, []);

  return {
    showTokensDialog,
    setShowTokensDialog,
    isProcessing,
    t,
    language,
    // PayPlus charges in ILS — pricing-page UIs that consume usePricing always display ₪.
    currencySymbol: ILS_SYMBOL,
    handleSelectPlan,
    handleAddTokens,
    handlePurchaseTokens,
    getTokenPriceDisplay,
    getTokenOriginalPriceDisplay,
    pendingDowngrade,
    handleConfirmDowngrade,
    handleCancelDowngrade,
    pendingReactivation,
    handleConfirmReactivation,
    handleCancelReactivation,
    currentPlanId,
    currentPeriodEnd,
    scheduledPlanId,
    scheduledBillingPeriod,
    handleCancelScheduledChange,
    showCancelScheduledDialog,
    confirmCancelScheduledChange,
    handleCloseCancelScheduledDialog,
    currentBillingPeriod,
    isLoggedIn: !!user,
    showCancelDialog,
    handleCancelComplete,
    handleCancelDialogClose,
    couponCode,
    setCouponCode,
    couponLoading,
    couponResult,
    handleApplyCoupon,
    handleClearCoupon,
    isDebugUser,
    handleDebugPurchase,
  };
}
